import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { postData } from "../../../utility/Utility";
import Swal from "sweetalert2";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

function EmployeeList() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const rowsPerPage = 10;

  const fetchEmployees = async (page = 1) => {
    setLoading(true);
    try {
      const payload = {
        page: page,
        limit: rowsPerPage,
        search: searchTerm || "",
        role: "employee",
        startDate: startDate || "",
        endDate: endDate || "",
      };
      const response = await postData("api/employees/list", payload);
      if (response?.status) {
        setEmployees(response.data || []);
        setTotalPages(response.pagination.totalPages || 0);
      }
    } catch (error) {
      console.error("Fetch employees error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees(currentPage + 1);
    // eslint-disable-next-line
  }, [currentPage]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchEmployees(1);
  };

  const handleReset = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(0);
    fetchEmployees(1);
  };

  // ✅ Delete employee
  const handleDeleteEmployee = async (employeeId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action will permanently delete the employee.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData("api/employees/delete-employee", {
          id: employeeId,
        });

        if (response?.status) {
          Swal.fire("Deleted!", "Employee has been deleted.", "success");
          fetchEmployees(currentPage + 1);
        } else {
          Swal.fire(
            "Error!",
            response?.message || "Failed to delete employee.",
            "error"
          );
        }
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire("Error!", "Something went wrong while deleting.", "error");
      }
    }
  };

  const handleStatusChange = async (employee) => {
    const newStatus = employee.status === "active" ? "inactive" : "active";

    const result = await Swal.fire({
      title: `Are you sure you want to ${
        newStatus === "active" ? "activate" : "deactivate"
      } this employee?`,
      text: `The employee will be ${
        newStatus === "active" ? "activated" : "deactivated"
      }.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${
        newStatus === "active" ? "activate" : "deactivate"
      } it!`,
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/employees/update/`, {
          employeeId: employee._id,
          name: employee.name,
          description: employee.description,
          email: employee.email,
          phone: employee.phone,
          logo: employee.logo,
          slug: employee.slug,
          status: newStatus,
        });

        if (response.status) {
          fetchEmployees(currentPage + 1);
        }
      } catch (error) {
        console.error("Status change error:", error);
      }
    }
  };

  return (
    <div className="content-page">
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Employee Management</h4>
              <p className="fw-semibold my-2">Employee List</p>
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/add-employee")}
                disabled={loading}
              >
                {loading ? "Adding..." : "Add Employee"}
              </button>
            </div>
          </div>

          {/* 🔍 Search + Filters */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-12 col-lg-3">
                        <label htmlFor="searchTerm" className="form-label">
                          Search
                        </label>
                        <input
                          type="text"
                          className="form-control ps-4"
                          placeholder="Search name/phone/company..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="col-md-12 col-lg-2">
                        <label htmlFor="startDate" className="form-label">
                          From Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="startDate"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>
                      <div className="col-md-12 col-lg-2">
                        <label htmlFor="endDate" className="form-label">
                          To Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="endDate"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          min={startDate}
                        />
                      </div>
                      <div className="col-md-12 col-lg-3 d-flex gap-2 mt-4 pt-3">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? "Searching..." : "Search"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* 📋 Employee Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">Loading employees...</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Selfie</th>
                            <th>Name</th>
                            <th>Employee ID</th>
                            <th>
                              Email
                              <br />
                              Mobile
                            </th>
                            <th>
                              DOB
                              <br />
                              Gender
                            </th>
                            <th>Designation</th>
                            <th>Job Status</th>
                            <th>Compensation Type</th>
                            <th>Salary Details</th>
                            <th>Created At</th>
                            <th width="180px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employees.length > 0 ? (
                            employees.map((employee, index) => (
                              <tr key={employee._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td>
                                  {employee.image ? (
                                    <img
                                      src={employee.image}
                                      alt="selfie"
                                      style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 8,
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : null}
                                </td>
                                <td>{employee.username}</td>
                                <td>{employee.employeeId}</td>
                                <td>
                                  {employee.email}
                                  <br />
                                  {employee.contactNumber}
                                </td>
                                <td>
                                  {employee.dob
                                    ? new Date(
                                        employee.dob
                                      ).toLocaleDateString()
                                    : ""}
                                  <br />
                                  {employee.gender}
                                </td>
                                <td>{employee.designation}</td>
                                <td>{employee.jobStatus || "-"}</td>
                                <td>{employee.compensationType || "-"}</td>
                                <td>
                                  {employee.salaryDetails.baseSalary || "-"}
                                  <br />
                                  {employee.salaryDetails.paymentFrequency ||
                                    "-"}
                                </td>
                                <td>
                                  {employee.createdAt
                                    ? new Date(
                                        employee.createdAt
                                      ).toLocaleDateString()
                                    : ""}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    {/* Toggle status */}
                                    <button
                                      className={`btn btn-sm btn-outline-${
                                        employee.status === "active"
                                          ? "warning"
                                          : "success"
                                      }`}
                                      onClick={() =>
                                        handleStatusChange(employee)
                                      }
                                      title={
                                        employee.status === "active"
                                          ? "Deactivate"
                                          : "Activate"
                                      }
                                    >
                                      <i
                                        className={`mdi mdi-${
                                          employee.status === "active"
                                            ? "close"
                                            : "check"
                                        }`}
                                      ></i>
                                    </button>

                                    {/* Add role */}
                                    
                                    {/* <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        navigate(`/add-role`, {
                                          state: { employee },
                                        })
                                      }
                                      title="Add Role"
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button> */}

                                    {/* 👁️ View / Update */}
                                    <button
                                      className="btn btn-sm btn-outline-info"
                                      onClick={() =>
                                        navigate("/add-employee", {
                                          state: { employee },
                                        })
                                      }
                                      title="View / Update"
                                    >
                                      <i className="mdi mdi-eye"></i>
                                    </button>

                                    {/* 🗑️ Delete */}
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() =>
                                        handleDeleteEmployee(employee._id)
                                      }
                                      title="Delete"
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="15" className="text-center py-4">
                                No employees found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={
                            <i className="mdi mdi-chevron-left"></i>
                          }
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeList;
